import { Injectable } from '@angular/core'
import { Observable, map, of } from 'rxjs'

import { Currency, CurrencySearchParams } from './currency.model'
import { Page } from '../../models/util.model'
import { difference, uniq } from 'lodash'
import { CURRENCIES_LIST } from '../../consts/currency.const'

@Injectable({
  providedIn: 'root',
})
export class CurrenciesService {
  /**
   * Search currencies by params
   * @param params - The search params
   * @returns The observable<Page<Currency>> for search currencies
   */
  search$(params?: CurrencySearchParams): Observable<Page<Currency>> {
    const sortedCurrencies = this.sortCurrencies(CURRENCIES_LIST)
    const filteredCurrencies = this.applyFilters(sortedCurrencies, params)

    const page: Page<Currency> = {
      data: filteredCurrencies,
      totalCount: CURRENCIES_LIST.length,
    }

    return of(page)
  }

  /**
   * List currencies by params
   * @param params - The search params
   * @returns The observable<Currency[]> for list currencies
   */
  list$(params?: CurrencySearchParams): Observable<Currency[]> {
    const sortedCurrencies = this.sortCurrencies(CURRENCIES_LIST)
    const filteredCurrencies = this.applyFilters(sortedCurrencies, params)
    return of(filteredCurrencies)
  }

  /**
   * List currencies by params
   * @param params - The search params
   * @returns The observable<Currency[]> for list currencies
   */
  cleanList(params?: CurrencySearchParams): Currency[] {
    const sortedCurrencies = this.sortCurrencies(CURRENCIES_LIST)
    const filteredCurrencies = this.applyFilters(sortedCurrencies, params)
    return filteredCurrencies
  }

  /**
   * Read currency by id
   * @param currencyId - The currency ID
   * @returns The observable<Currency> for read the currency
   */
  read$(currencyCode: string): Observable<Currency | undefined> {
    return of(CURRENCIES_LIST.find((c) => c.code === currencyCode))
  }

  /**
   * Generate currencies store
   * @param currencyKeys - the currency keys to load
   * @param currencies - the currencies already loaded
   * @returns the Observable<Currency[]> as store
   */
  store$(
    currencyKeys: string[],
    currencies: Currency[],
    key: 'code' = 'code',
  ): Observable<Currency[]> {
    currencyKeys = uniq(currencyKeys)
    currencyKeys = difference(
      currencyKeys,
      currencies.map((u) => u[key]),
    )

    if (currencyKeys.length === 0) {
      return of(currencies)
    }

    return this.list$({ [key]: currencyKeys }).pipe(
      map((crs) => [...currencies, ...crs]),
    )
  }

  /**
   * Apply filters to currencies list
   * @param currencies - The currencies to filter
   * @param params - The search params
   * @returns The filtered currencies
   */
  private applyFilters(
    currencies: Currency[],
    params?: CurrencySearchParams,
  ): Currency[] {
    let filteredCurrencies = [...currencies]

    if (!params) {
      return filteredCurrencies
    }

    if (params['name:ct']) {
      const searchTerm = params['name:ct'].toLowerCase()
      filteredCurrencies = filteredCurrencies.filter((currency) =>
        currency.name.toLowerCase().includes(searchTerm),
      )
    }

    if (params.name) {
      const names = Array.isArray(params.name) ? params.name : [params.name]
      filteredCurrencies = filteredCurrencies.filter((currency) =>
        names.some((name) =>
          currency.name.toLowerCase().includes(name.toLowerCase()),
        ),
      )
    }

    if (params['code:ct']) {
      const searchTerm = params['code:ct'].toLowerCase()
      filteredCurrencies = filteredCurrencies.filter((currency) =>
        currency.code.toLowerCase().includes(searchTerm),
      )
    }

    if (params.code) {
      const codes = Array.isArray(params.code) ? params.code : [params.code]
      filteredCurrencies = filteredCurrencies.filter((currency) =>
        codes.some((code) =>
          currency.code.toLowerCase().includes(code.toLowerCase()),
        ),
      )
    }

    return filteredCurrencies
  }

  /**
   * Sort currencies alphabetically by name
   * @param currencies - The currencies to sort
   * @returns The sorted currencies
   */
  private sortCurrencies(currencies: Currency[]): Currency[] {
    return currencies.sort((a, b) => a.name.localeCompare(b.name))
  }
}
